import React from 'react'
import { Helmet } from 'react-helmet-async'
import { t } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'

// Base SEO props interface
interface SEOProps {
  title?: string
  description?: string
  keywords?: string
  canonicalUrl?: string
  ogImage?: string
  twitterImage?: string
  children?: React.ReactNode
}

// Language to region mapping - without specific region for English
const languageToRegion: Record<string, { code: string; position: string; icbm: string }> = {
  mongolian: { code: 'MN', position: '47.8864;106.9057', icbm: '47.8864, 106.9057' },
  portuguese: { code: 'PT', position: '39.3999;-8.2245', icbm: '39.3999, -8.2245' },
}

// Language to locale mapping
const languageToLocale: Record<string, string> = {
  en: 'en_GB', // Keeping GB locale for English display purposes
  mongolian: 'mn_MN',
  portuguese: 'pt_PT',
}

// Base SEO component with default values that match your landing page
export const SEO: React.FC<SEOProps> = ({
  title = t`RushB.GG - Get Your Daily Gems Tomorrow! Play Games & Win Big | Best Gaming Platform`,
  description = t`Tomorrow is a new day! Collect your daily gems on RushB.GG, join exciting gaming activities, and win amazing rewards. Your gaming time, your gem rewards.`,
  keywords = t`daily gems, daily check-in, gaming rewards, gem collection, tomorrow gems, gaming platform, daily tasks, gem rewards, gaming benefits, check-in rewards`,
  canonicalUrl = 'https://rush-b.gg',
  ogImage = 'https://superskin.gg/static/media/LOGO-beta.b39da59445339cc538bf.png',
  twitterImage = 'https://superskin.gg/static/media/LOGO-beta.b39da59445339cc538bf.png',
  children,
}) => {
  // Get current language from lingui
  const { i18n } = useLingui()
  const currentLanguage = i18n.locale || 'en'

  // Get region information based on current language (if available)
  const regionInfo = languageToRegion[currentLanguage]
  const locale = languageToLocale[currentLanguage] || 'en_GB'

  // Format title for meta tags (removing any existing formattedTitle reference)
  const metaTitle = title

  // Get language display name for the "language" meta tag
  const languageDisplayNames: Record<string, string> = {
    en: 'English',
    mongolian: 'Mongolian',
    portuguese: 'Portuguese',
  }
  const languageDisplay = languageDisplayNames[currentLanguage] || 'English'

  // Update canonical URL with language path if needed
  const fullCanonicalUrl = canonicalUrl.endsWith('/')
    ? `${canonicalUrl}${currentLanguage}`
    : `${canonicalUrl}/${currentLanguage}`

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content="SuperSkin.GG" />
      <meta charSet="utf-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <meta name="theme-color" content="#FFE03F" />
      <meta name="darkreader-lock" />
      <meta name="robots" content="index, follow, max-image-preview:large" />

      {/* Language - Updated with i18n values */}
      <meta name="language" content={languageDisplay} />
      <meta name="revisit-after" content="1 day" />
      <meta name="rating" content="general" />

      {/* Region tags - Only added for specific languages that have region associations */}
      {regionInfo && (
        <>
          <meta name="geo.region" content={regionInfo.code} />
          <meta name="geo.position" content={regionInfo.position} />
          <meta name="ICBM" content={regionInfo.icbm} />
        </>
      )}

      {/* Open Graph Tags - Updated with i18n locale */}
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={fullCanonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="SuperSkin.GG" />
      <meta property="og:locale" content={locale} />

      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={metaTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={twitterImage} />
      <meta name="twitter:creator" content="@superskingg" />

      {/* Links - Updated with language path */}
      <link rel="canonical" href={fullCanonicalUrl} />

      <meta name="google-site-verification" content="XvQIONUNcZUkuFLjr2vdgqwb82iRHD1_JxZMaiq4Fhk" />

      {/* Additional tags passed as children */}
      {children}
    </Helmet>
  )
}
